import { useQuery } from "@apollo/client";
import { Col, Empty, Form, Row, Table } from "antd";
import Search from "antd/lib/input/Search";
import { addDays, format, subDays } from "date-fns";
import React, { useEffect, useState } from "react";
import DateRange from "../components/date_range";
import Error from "../components/apollo_error";
import LoadingSpinner from "../components/loading_indicator";
import withClinic from "../hooks/with_clinic";
import { PaginationConfig } from "antd/lib/pagination";
import { Typography } from "antd";
import {
  get_system_logs,
  get_total_logs_count,
  getSystemLogsVar,
} from "../graphql/system_log";
import { ApolloResult, SystemLog } from "../store";

interface Metadata {
  comment?: string;
  type?: string;
  action?: string;
  [key: string]: any;
}

interface Log extends Omit<SystemLog, "metadata"> {
  metadata?: Metadata;
  detail: string;
}

interface P {
  currentClinic?: {
    id: string;
    code: string;
  };
}

const SystemLogPage = (props: P) => {
  const clinicId = props.currentClinic?.id || "";
  const [logsData, setLogsData] = useState<Log[]>([]);
  const [searchText, setSearchText] = useState<string | null>(null);
  const [logTypeFilter, setLogTypeFilter] = useState<string[]>([]);
  const [pagination, setPagination] = useState<PaginationConfig | null>(null);
  const [ranges, setRanges] = useState({
    startDate: subDays(new Date(), 15),
    endDate: addDays(new Date(), 15),
  });
  const [form] = Form.useForm();

  const take = pagination?.pageSize || 20;
  const skip = ((pagination?.current || 1) - 1) * take;

  const variables = getSystemLogsVar({
    clinicId,
    startDate: ranges.startDate,
    endDate: ranges.endDate,
    searchText,
    take,
    skip,
    logType: logTypeFilter,
  });

  const { loading, data, error } = useQuery<
    ApolloResult<"systemLogs", SystemLog[]>
  >(get_system_logs, { variables });
  const totalCount = useQuery<
    ApolloResult<"aggregateSystemLog", { _count: { id: number } }>
  >(get_total_logs_count, { variables });

  useEffect(() => {
    if (data?.systemLogs) {
      const logs = data.systemLogs.map((b) => {
        let metadata: Metadata = {};
        try {
          metadata =
            typeof b.metadata === "string"
              ? JSON.parse(b.metadata || "{}")
              : b.metadata || {};
        } catch {
          metadata = {};
        }
        const varName =
          metadata?.type === "order" &&
          metadata?.member?.clinic_members?.length > 0
            ? metadata?.member?.clinic_members[0]?.name
            : metadata?.member?.name;
        const name = varName != null ? `${varName} | ` : "";

        return {
          ...b,
          metadata,
          detail:
            metadata.type === "member"
              ? metadata?.context?.phoneNumber ?? ""
              : metadata.type === "member_service_bag"
              ? `${metadata?.service?.name} (${metadata?.remaining_count}/ ${metadata?.original_count})`
              : metadata?.type === "order"
              ? `${name}${metadata?.order_id} | ${
                  metadata?.created_at &&
                  format(new Date(metadata?.created_at), "dd MMM,yyyy")
                } | ${metadata?.payment_method}`
              : metadata?.type === "MedicalRecord"
              ? [
                  metadata?.title,
                  metadata?.recordType,
                  Array.isArray(metadata?.detailIds)
                    ? `${metadata.detailIds.length} details`
                    : null,
                  Array.isArray(metadata?.documentIds)
                    ? `${metadata.documentIds.length} docs`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" | ")
              : metadata?.type === "MedicalRecordDetail"
              ? [
                  metadata?.title,
                  Array.isArray(metadata?.documentIds)
                    ? `${metadata.documentIds.length} docs`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" | ")
              : metadata?.type === "MedicalRecordDocument"
              ? metadata?.fileName || metadata?.entityId || ""
              : metadata?.type === "ConsultationNote" ||
                metadata?.type === "consultation_note"
              ? [
                  metadata?.title || metadata?.entityId,
                  metadata?.sourceType,
                  metadata?.templateKey
                    ? `Template: ${metadata.templateKey}`
                    : null,
                  metadata?.revision !== undefined &&
                  metadata?.revision !== null
                    ? `Rev: ${metadata.revision}`
                    : null,
                  Array.isArray(metadata?.documentIds)
                    ? `${metadata.documentIds.length} docs`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" | ")
              : metadata?.type === "ConsultationNoteDocument"
              ? metadata?.fileName || metadata?.entityId || ""
              : "",
        };
      });
      setLogsData(logs);
    }
  }, [data]);

  if (loading) return <LoadingSpinner />;
  if (error) return <Error error={error} />;
  if (!data) return <Empty />;

  const onDateChange = (startDate: Date, endDate: Date) => {
    setRanges({ startDate, endDate });
  };

  const columns: any[] = [
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      editable: false,
      width: 170,
      ellipsis: true,
      sorter: (a: Log, b: Log) =>
        new Date(a.created_at || "").getTime() -
        new Date(b.created_at || "").getTime(),
      render: (v: Log, r: Log) => {
        return r.created_at
          ? format(new Date(r.created_at), "dd MMM,yyyy h:mm a")
          : "N/A";
      },
    },
    {
      title: "Type",
      dataIndex: "metadata",
      key: "type",
      editable: false,
      width: 140,
      ellipsis: true,
      sorter: (a: Log, b: Log) =>
        a.log_id.toLowerCase().localeCompare(b.log_id.toLowerCase()),
      render: (v: string, r: Log) => {
        return (r.metadata?.type || "")
          .replace(/([a-z])([A-Z])/g, "$1 $2")
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      },
    },
    {
      title: "Action",
      dataIndex: "metadata",
      key: "action",
      editable: false,
      width: 140,
      ellipsis: true,
      sorter: (a: Log, b: Log) =>
        a.log_id.toLowerCase().localeCompare(b.log_id.toLowerCase()),
      render: (v: string, r: Log) => {
        const action = String(r.metadata?.action || "");
        if (!action) return "";
        return action.charAt(0).toUpperCase() + action.slice(1).toLowerCase();
      },
    },
    {
      title: "Comment",
      dataIndex: "metadata",
      key: "comment",
      editable: false,
      width: 140,
      sorter: (a: Log, b: Log) =>
        a.log_id.toLowerCase().localeCompare(b.log_id.toLowerCase()),
      render: (v: string, r: Log) => {
        return (
          <Typography.Paragraph ellipsis={{ rows: 2, expandable: true }}>
            {r.metadata?.comment || ""}
          </Typography.Paragraph>
        );
      },
    },
    {
      title: "Detail",
      dataIndex: "metadata",
      key: "detail",
      editable: false,
      width: 150,
      sorter: (a: Log, b: Log) =>
        (a.user?.name || "")
          .toLowerCase()
          .localeCompare((b.user?.name || "").toLowerCase()),
      render: (v: number, r: Log) => {
        return (
          <Typography.Paragraph ellipsis={{ rows: 2, expandable: true }}>
            {r.detail}
          </Typography.Paragraph>
        );
      },
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      editable: false,
      width: 150,
      sorter: (a: Log, b: Log) =>
        (a.user?.name || "")
          .toLowerCase()
          .localeCompare((b.user?.name || "").toLowerCase()),
      render: (v: number, r: Log) => {
        return r.user?.name || "System";
      },
    },
  ];

  const control = (
    <Row style={{ marginTop: -18 }}>
      <Col flex={4}>
        <Row>
          <Col span={8}>Logs</Col>
          <Col span={8} offset={8}>
            <Search
              defaultValue={searchText || undefined}
              placeholder={`search ${
                totalCount.data?.aggregateSystemLog._count.id || 0
              }...`}
              allowClear
              size="middle"
              onSearch={(val: any) => setSearchText(val)}
            />
          </Col>
        </Row>
      </Col>
      <Col flex={0}>
        <DateRange
          startDate={ranges.startDate}
          endDate={ranges.endDate}
          onSelect={onDateChange}
        />
      </Col>
    </Row>
  );

  const context = (
    <Row style={{ marginTop: 8 }}>
      <Col span={24}>
        <Form form={form} component={false}>
          <Table
            showHeader={logsData.length > 0}
            dataSource={logsData.map((log: Log) => ({
              key: log.id,
              ...log,
            }))}
            columns={columns}
            rowClassName="editable-row"
            onChange={(p: PaginationConfig) => {
              setPagination(p);
            }}
            pagination={{
              total: totalCount.data?.aggregateSystemLog._count.id || 0,
              current: pagination?.current,
              pageSize: pagination?.pageSize,
              defaultPageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
          />
        </Form>
      </Col>
    </Row>
  );

  // const filters = (
  //   <Row style={{ marginTop: 4, marginRight: 2 }}>
  //     <Col span={16}>
  //       <MultiSelect
  //         selected={logTypeFilter}
  //         placeholder="Select log types"
  //         options={["TRACE", "DEBUG", "INFO", "WARN", "ERROR", "FATAL"]}
  //         onChange={setLogTypeFilter}
  //       />
  //     </Col>
  //   </Row>
  // );

  return (
    <React.Fragment>
      {control}
      {/* {filters} */}
      {context}
    </React.Fragment>
  );
};

export default withClinic(SystemLogPage);
